import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

// Validate required environment variables
if (!BACKEND_URL) {
  console.error('REACT_APP_BACKEND_URL environment variable is required');
}
if (!ADMIN_PASSWORD) {
  console.error('REACT_APP_ADMIN_PASSWORD environment variable is required');
}
if (!ADMIN_KEY) {
  console.error('REACT_APP_ADMIN_KEY environment variable is required');
}

const AdminPage = () => {
  useEffect(() => {
    // Inject styles
    const style = document.createElement('style');
    style.textContent = `
      .bm-btn {
        padding: 4px 10px;
        border-radius: 6px;
        border: none;
        cursor: pointer;
        font-size: 12px;
        margin-right: 4px;
      }
      .bm-btn-review { background: #e2e8f0; color: #0f172a; }
      .bm-btn-review:hover { background: #cbd5e1; }
      .bm-btn-approve { background: #22c55e; color: #fff; }
      .bm-btn-approve:hover { background: #16a34a; }
      .bm-btn-deny { background: #ef4444; color: #fff; }
      .bm-btn-deny:hover { background: #dc2626; }
      .bm-modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15,23,42,0.45);
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      }
      .bm-modal {
        background: #fff;
        max-width: 560px;
        width: 95%;
        border-radius: 14px;
        padding: 20px 20px 16px 20px;
        box-shadow: 0 20px 35px rgba(15, 23, 42, 0.18);
        max-height: 80vh;
        overflow-y: auto;
      }
      .bm-modal h3 { margin-top: 0; color: #0f172a; }
      .bm-modal-row { 
        margin-bottom: 8px; 
        font-size: 14px; 
        padding: 6px;
        border-bottom: 1px solid #f1f5f9;
      }
      .bm-modal-row span { 
        font-weight: 600; 
        display: inline-block; 
        width: 150px;
        color: #1E3A8A;
      }
    `;
    document.head.appendChild(style);

    let currentApplications = [];
    let currentProjects = [];
    let currentBmLeads = [];

    // Archive state
    let showArchived = false;
    let showBmApproved = false;
    
    const toggleArchive = () => {
      showArchived = !showArchived;
      showBmApproved = false; // Reset BM approved when toggling archive
      
      const toggleBtn = document.getElementById('archiveToggle');
      if (toggleBtn) {
        toggleBtn.textContent = showArchived ? 'Show Active' : 'Show Archive';
        toggleBtn.style.background = showArchived ? '#667eea' : '#e2e8f0';
        toggleBtn.style.color = showArchived ? '#fff' : '#0f172a';
      }
      
      const bmApprovedBtn = document.getElementById('bmApprovedToggle');
      if (bmApprovedBtn) {
        bmApprovedBtn.textContent = 'Show BM Approved';
        bmApprovedBtn.style.background = '#e2e8f0';
        bmApprovedBtn.style.color = '#0f172a';
      }
      
      loadAllData();
    };
    
    const toggleBmApproved = () => {
      showBmApproved = !showBmApproved;
      showArchived = false; // Reset archive when toggling BM approved
      
      const bmApprovedBtn = document.getElementById('bmApprovedToggle');
      if (bmApprovedBtn) {
        bmApprovedBtn.textContent = showBmApproved ? 'Show Active' : 'Show BM Approved';
        bmApprovedBtn.style.background = showBmApproved ? '#10B981' : '#e2e8f0';
        bmApprovedBtn.style.color = showBmApproved ? '#fff' : '#0f172a';
      }
      
      const archiveBtn = document.getElementById('archiveToggle');
      if (archiveBtn) {
        archiveBtn.textContent = 'Show Archive';
        archiveBtn.style.background = '#e2e8f0';
        archiveBtn.style.color = '#0f172a';
      }
      
      loadBmLeads();
    };

    const renderAssistantApplications = (data) => {
      const tbody = document.getElementById('applicationRows');
      tbody.innerHTML = '';
      
      // Filter for assistant applications (not client projects)
      let apps = data.filter(item => !item.form_type || item.form_type !== 'Client Project');
      
      // Filter based on archive state
      if (showArchived) {
        apps = apps.filter(item => item.status === 'Denied' || item.status === 'Archived');
      } else {
        apps = apps.filter(item => item.status !== 'Denied' && item.status !== 'Archived');
      }
      
      if (!apps.length) {
        const msg = showArchived ? 'No archived applications' : 'No applications yet…';
        tbody.innerHTML = `<tr><td colspan='8' style='text-align:center;color:#94a3b8;'>${msg}</td></tr>`;
        return;
      }
      
      apps.forEach(item => {
        const tr = document.createElement('tr');
        
        // Show different actions based on archive state
        let actions = '';
        if (showArchived) {
          actions = `<button class='bm-btn bm-btn-review' onclick='window.reviewItem("application","${item.id}")'>View</button>`;
        } else {
          actions = `
            <button class='bm-btn bm-btn-review' onclick='window.reviewItem("application","${item.id}")'>Review</button>
            <button class='bm-btn bm-btn-approve' onclick='window.updateStatus("application","${item.id}","Approved")'>Approve</button>
            <button class='bm-btn bm-btn-deny' onclick='window.updateStatus("application","${item.id}","Archived")'>Deny</button>
          `;
        }
        
        tr.innerHTML = `
          <td>${item.applicant_id || ''}</td>
          <td>${item.full_name || ''}</td>
          <td>${item.email || ''}</td>
          <td>${item.role || ''}</td>
          <td>${item.availability || ''}</td>
          <td>${item.location || ''}</td>
          <td><span style="padding:2px 8px;border-radius:4px;background:#e2e8f0;font-size:12px;">${item.status || 'New'}</span></td>
          <td style="white-space:nowrap;">${actions}</td>
        `;
        tbody.appendChild(tr);
      });
    };

    const renderClientProjects = (data) => {
      const tbody = document.getElementById('clientRequestRows');
      tbody.innerHTML = '';
      
      // Filter for client projects
      let projects = data.filter(item => item.form_type === 'Client Project');
      
      // Filter based on archive state
      if (showArchived) {
        projects = projects.filter(item => item.status === 'Denied' || item.status === 'Archived');
      } else {
        projects = projects.filter(item => item.status !== 'Denied' && item.status !== 'Archived');
      }
      
      if (!projects.length) {
        const msg = showArchived ? 'No archived project requests' : 'No client project requests yet…';
        tbody.innerHTML = `<tr><td colspan='8' style='text-align:center;color:#94a3b8;'>${msg}</td></tr>`;
        return;
      }
      
      projects.forEach(item => {
        const tr = document.createElement('tr');
        
        // Debug: Check if item has id field
        if (!item.id) {
          console.warn(`⚠️  Project ${item.client_id || 'UNKNOWN'} is missing 'id' field:`, item);
        }
        
        // Determine category
        const category = item.request_category === 'mini_services' ? 'Mini Services' : 'Full Project';
        const categoryBadge = category === 'Mini Services' 
          ? `<span style="padding:2px 8px;border-radius:4px;background:#FEF2F2;color:#DC2626;font-size:12px;font-weight:600;">Mini Services</span>`
          : `<span style="padding:2px 8px;border-radius:4px;background:#EFF6FF;color:#1E3A8A;font-size:12px;font-weight:600;">Full Project</span>`;
        
        // Handle project type or services
        let typeServices = '';
        if (item.request_category === 'mini_services') {
          const services = item.selected_services || [];
          if (Array.isArray(services) && services.length > 0) {
            typeServices = services.join(', ');
          } else {
            typeServices = 'Services not specified';
          }
        } else {
          typeServices = Array.isArray(item.project_type) ? item.project_type.join(', ') : (item.project_type || 'Not specified');
        }
        
        // Show different actions based on archive state
        let actions = '';
        const itemId = item.id || item.client_id || 'undefined';  // Fallback to client_id if id is missing
        
        if (!item.id) {
          // If no id, show error button
          actions = `<button class='bm-btn' style='background:#EF4444;color:#fff;cursor:not-allowed;' title='Missing ID field'>Error: No ID</button>`;
        } else if (showArchived) {
          actions = `<button class='bm-btn bm-btn-review' onclick='window.reviewItem("project","${itemId}")'>View</button>`;
        } else {
          actions = `
            <button class='bm-btn bm-btn-review' onclick='window.reviewItem("project","${itemId}")'>Review</button>
            <button class='bm-btn bm-btn-approve' onclick='window.updateStatus("project","${itemId}","Approved")'>Approve</button>
            <button class='bm-btn bm-btn-deny' onclick='window.updateStatus("project","${itemId}","Archived")'>Deny</button>
          `;
        }
        
        tr.innerHTML = `
          <td>${item.client_id || ''}</td>
          <td>${categoryBadge}</td>
          <td>${item.full_name || ''}</td>
          <td>${item.email || ''}</td>
          <td>${item.business_name || ''}</td>
          <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${typeServices}">${typeServices}</td>
          <td>${item.budget || ''}</td>
          <td style="white-space:nowrap;">${actions}</td>
        `;
        tbody.appendChild(tr);
      });
    };

    const loadApplicationsAndProjects = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/applications`, {
          headers: { 'x-admin-key': ADMIN_KEY }
        });
        // Both response formats work
        const data = await res.json();
        
        // Store in global variables with PROPER STATUS FILTERING
        currentApplications = data.filter(item => !item.form_type || item.form_type !== 'Client Project');
        currentProjects = data.filter(item => item.form_type === 'Client Project');
        
        // Filter by status based on showArchived flag
        let filteredApplications = currentApplications;
        let filteredProjects = currentProjects;
        
        if (showArchived) {
          // Show only archived/denied records
          filteredApplications = currentApplications.filter(item => 
            item.status === 'Archived' || item.status === 'Denied'
          );
          filteredProjects = currentProjects.filter(item => 
            item.status === 'Archived' || item.status === 'Denied'
          );
        } else {
          // Show only ACTIVE records (not archived/denied)
          filteredApplications = currentApplications.filter(item => 
            item.status !== 'Archived' && item.status !== 'Denied'
          );
          filteredProjects = currentProjects.filter(item => 
            item.status !== 'Archived' && item.status !== 'Denied'
          );
        }
        
        // Render both tables with FILTERED data
        renderAssistantApplications(filteredApplications);
        renderClientProjects(filteredProjects);
      } catch (err) {
        console.error('Error loading applications:', err);
      }
    };

    const loadBmLeads = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/bm-leads`, {
          headers: { 'x-admin-key': ADMIN_KEY }
        });
        const data = await res.json();
        
        // Filter to only show BM lead submissions
        let bmLeadsOnly = Array.isArray(data)
          ? data.filter(item => item.form_type === 'BM lead')
          : [];
        
        currentBmLeads = bmLeadsOnly;
        
        // Filter based on view state
        if (showBmApproved) {
          // Show only BM Approved leads
          bmLeadsOnly = bmLeadsOnly.filter(item => item.status === 'BM Approved');
        } else if (showArchived) {
          // Show only archived/denied BM leads
          bmLeadsOnly = bmLeadsOnly.filter(item => item.status === 'Denied' || item.status === 'Archived');
        } else {
          // Show active leads (not approved and not archived)
          bmLeadsOnly = bmLeadsOnly.filter(item => 
            item.status !== 'BM Approved' && 
            item.status !== 'Denied' && 
            item.status !== 'Archived'
          );
        }
        
        const tbody = document.getElementById('bmLeadRows');
        tbody.innerHTML = '';
        
        if (!bmLeadsOnly.length) {
          let msg = 'No BM leads yet…';
          if (showBmApproved) {
            msg = 'No approved BM leads yet…';
          } else if (showArchived) {
            msg = 'No archived BM leads…';
          }
          tbody.innerHTML = `<tr><td colspan='6' style='text-align:center;color:#94a3b8;'>${msg}</td></tr>`;
          return;
        }
        
        bmLeadsOnly.forEach(item => {
          const tr = document.createElement('tr');
          
          // Show different actions based on view state
          let actions = '';
          if (showBmApproved || showArchived) {
            // Read-only view - only Review button
            actions = `<button class='bm-btn bm-btn-review' onclick='window.reviewItem("bm","${item.id}")'>View</button>`;
          } else {
            // Active view - all actions
            actions = `
              <button class='bm-btn bm-btn-review' onclick='window.reviewItem("bm","${item.id}")'>Review</button>
              <button class='bm-btn bm-btn-approve' onclick='window.updateStatus("bm","${item.id}","BM Approved")'>Approve</button>
              <button class='bm-btn bm-btn-deny' onclick='window.updateStatus("bm","${item.id}","Archived")'>Deny</button>
            `;
          }
          
          tr.innerHTML = `
            <td>${item.full_name || ''}</td>
            <td>${item.email || ''}</td>
            <td>${item.business_name || ''}</td>
            <td>${item.service_level || ''}</td>
            <td>${item.monthly_budget || ''}</td>
            <td style="white-space:nowrap;">${actions}</td>
          `;
          tbody.appendChild(tr);
        });
      } catch (err) {
        console.error('Error loading BM leads:', err);
      }
    };

    const loadAllData = () => {
      loadApplicationsAndProjects();
      loadBmLeads();
    };

    const reviewItem = (type, id) => {
      let item;
      if (type === 'application') {
        item = currentApplications.find(x => x.id === id);
      } else if (type === 'project') {
        item = currentProjects.find(x => x.id === id);
      } else if (type === 'bm') {
        item = currentBmLeads.find(x => x.id === id);
      }
      if (!item) return;
      
      const body = document.getElementById('bmModalBody');
      const title = document.getElementById('bmModalTitle');
      if (type === 'application') {
        title.textContent = 'Application Details';
      } else if (type === 'project') {
        const category = item.request_category === 'mini_services' ? 'Mini Services' : 'Full Project';
        title.textContent = `Client Request Details - ${category}`;
      } else if (type === 'bm') {
        title.textContent = 'BM Lead Details';
      }
      body.innerHTML = '';
      
      // Custom display for project requests
      if (type === 'project') {
        const category = item.request_category === 'mini_services' ? 'Mini Services' : 'Full Project';
        body.innerHTML += `<div class='bm-modal-row'><span>Category:</span> <strong style="color: ${item.request_category === 'mini_services' ? '#DC2626' : '#1E3A8A'};">${category}</strong></div>`;
        
        if (item.request_category === 'mini_services' && item.selected_services) {
          const services = Array.isArray(item.selected_services) ? item.selected_services : [];
          body.innerHTML += `<div class='bm-modal-row'><span>Selected Services:</span> ${services.join(', ') || 'None'}</div>`;
        } else if (item.project_type) {
          body.innerHTML += `<div class='bm-modal-row'><span>Project Type:</span> ${item.project_type}</div>`;
          if (item.project_type_notes) {
            body.innerHTML += `<div class='bm-modal-row'><span>Notes:</span> ${item.project_type_notes}</div>`;
          }
        }
        
        // Display other fields
        const fieldsToShow = ['full_name', 'email', 'phone', 'business_name', 'business_type', 'main_goal', 'budget', 'timeline', 'current_links', 'ad_accounts', 'notes', 'status', 'client_id'];
        fieldsToShow.forEach(key => {
          if (item[key]) {
            const value = Array.isArray(item[key]) ? item[key].join(', ') : item[key];
            body.innerHTML += `<div class='bm-modal-row'><span>${key}:</span> ${value}</div>`;
          }
        });
      } else {
        Object.keys(item).forEach(key => {
          const value = Array.isArray(item[key]) ? item[key].join(', ') : (item[key] || '');
          body.innerHTML += `<div class='bm-modal-row'><span>${key}:</span> ${value}</div>`;
        });
      }
      
      document.getElementById('bmModalOverlay').style.display = 'flex';
    };

    const closeModal = () => {
      document.getElementById('bmModalOverlay').style.display = 'none';
    };

    const updateStatus = async (type, id, status) => {
      // Find the item to get its display info
      let item;
      if (type === 'application') {
        item = currentApplications.find(x => x.id === id);
      } else if (type === 'project') {
        item = currentProjects.find(x => x.id === id);
      } else if (type === 'bm') {
        item = currentBmLeads.find(x => x.id === id);
      }
      
      const displayId = item?.client_id || item?.applicant_id || id;
      console.log(`Attempting to update ${type} ${displayId} (internal id: ${id}) to status: ${status}`);
      
      let endpoint;
      if (type === 'application' || type === 'project') {
        // Both applications and client projects now use the unified endpoint
        endpoint = `${BACKEND_URL}/api/applications/${id}`;
      } else if (type === 'bm') {
        endpoint = `${BACKEND_URL}/api/bm-leads/${id}`;
      }

      try {
        const res = await fetch(endpoint, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': ADMIN_KEY
          },
          body: JSON.stringify({ status })
        });
        
        if (res.ok) {
          console.log(`✅ ${type} ${displayId} status updated to ${status}`);
          // Reload tables to show updated data including new IDs from backend
          await loadAllData();
        } else {
          const errorText = await res.text();
          console.error(`❌ Failed to update ${displayId}: ${res.status} ${res.statusText}`);
          console.error(`Response: ${errorText}`);
          alert(`Failed to archive ${displayId}. Error: ${res.status} ${res.statusText}\nCheck console for details.`);
        }
      } catch (err) {
        console.error('Error updating status:', err);
      }
    };

    // Make functions available globally
    window.loadAllData = loadAllData;
    window.reviewItem = reviewItem;
    window.closeModal = closeModal;
    window.updateStatus = updateStatus;
    window.toggleArchive = toggleArchive;
    window.toggleBmApproved = toggleBmApproved;

    window.currentApplications = currentApplications;
    window.currentProjects = currentProjects;
    window.currentBmLeads = currentBmLeads;

    // Load data immediately (auth is handled by AdminAuthGateUserPass wrapper)
    loadAllData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold" style={{ color: '#1E3A8A' }}>
                BM-Assist.co
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-700 hover:text-[#1E3A8A] transition-colors">
                ← Back to site
              </Link>
              <Link to="/applications" className="text-gray-700 hover:text-[#1E3A8A] transition-colors">
                Applications
              </Link>
              <Link to="/request" className="text-gray-700 hover:text-[#1E3A8A] transition-colors">
                Client Requests
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Admin Content (auth handled by wrapper) */}
      <div 
        id='adminContent' 
        style={{ 
          padding: '100px 20px 60px 20px', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ color: '#1E3A8A', marginBottom: '4px', fontSize: '2.5rem', fontWeight: 'bold' }}>
              BM Internal Dashboard
            </h1>
            <p style={{ color: '#475569', marginBottom: '0', fontSize: '1.1rem' }}>
              Review, approve, or deny assistant applications and client project requests.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => window.location.href = '/admin/ops'}
              style={{
                padding: '12px 24px',
                background: '#10B981',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#059669'}
              onMouseOut={(e) => e.target.style.background = '#10B981'}
            >
              🎯 Admin Ops Control Room
            </button>
            <button 
              onClick={() => window.location.href = '/admin/assistants'}
              style={{
                padding: '10px 20px',
                background: '#3B82F6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#2563EB'}
              onMouseOut={(e) => e.target.style.background = '#3B82F6'}
            >
              Manage Assistants
            </button>
            <button 
              onClick={() => window.location.href = '/admin/executives'}
              style={{
                padding: '10px 20px',
                background: '#3B82F6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#2563EB'}
              onMouseOut={(e) => e.target.style.background = '#3B82F6'}
            >
              Manage Executives
            </button>
            <button 
              onClick={() => window.location.href = '/admin/travel-archive'}
              style={{
                padding: '10px 20px',
                background: '#F59E0B',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#D97706'}
              onMouseOut={(e) => e.target.style.background = '#F59E0B'}
            >
              Travel Archive
            </button>
            <button 
              onClick={() => window.location.href = '/cadmin'}
              style={{
                padding: '10px 20px',
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#5568d3'}
              onMouseOut={(e) => e.target.style.background = '#667eea'}
            >
              Open Client Dashboard Admin
            </button>
            <button 
              id='bmApprovedToggle'
              onClick={() => window.toggleBmApproved()}
              style={{
                padding: '10px 20px',
                background: '#e2e8f0',
                color: '#0f172a',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              Show BM Approved
            </button>
            <button 
              id='archiveToggle'
              onClick={() => window.toggleArchive()}
              style={{
                padding: '10px 20px',
                background: '#e2e8f0',
                color: '#0f172a',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              Show Archive
            </button>
          </div>
        </div>

        {/* Assistant Applications Table */}
        <h2 style={{ marginTop: '20px', marginBottom: '10px', color: '#0F172A', fontSize: '1.5rem', fontWeight: 'bold' }}>
          Assistant Applications
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table 
            border='1' 
            width='100%' 
            cellSpacing='0' 
            cellPadding='10' 
            style={{ 
              borderCollapse: 'collapse', 
              borderColor: '#e2e8f0', 
              fontFamily: 'sans-serif',
              fontSize: '14px'
            }}
          >
            <thead style={{ background: '#1E3A8A', color: '#fff' }}>
              <tr>
                <th>Applicant ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Availability</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id='applicationRows'>
              <tr>
                <td colSpan='8' style={{ textAlign: 'center', color: '#94a3b8' }}>
                  No applications loaded yet…
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Client Project Requests Table */}
        <h2 style={{ marginTop: '40px', marginBottom: '10px', color: '#0F172A', fontSize: '1.5rem', fontWeight: 'bold' }}>
          Client Project Requests
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table 
            border='1' 
            width='100%' 
            cellSpacing='0' 
            cellPadding='10' 
            style={{ 
              borderCollapse: 'collapse', 
              borderColor: '#e2e8f0', 
              fontFamily: 'sans-serif',
              fontSize: '14px'
            }}
          >
            <thead style={{ background: '#1E3A8A', color: '#fff' }}>
              <tr>
                <th>Client ID</th>
                <th>Category</th>
                <th>Name</th>
                <th>Email</th>
                <th>Business</th>
                <th>Type/Services</th>
                <th>Budget</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id='clientRequestRows'>
              <tr>
                <td colSpan='8' style={{ textAlign: 'center', color: '#94a3b8' }}>
                  No client requests loaded yet…
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* BM Leads Table */}
        <h2 style={{ marginTop: '40px', marginBottom: '10px', color: '#0F172A', fontSize: '1.5rem', fontWeight: 'bold' }}>
          BM Leads (full-service)
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table 
            border='1' 
            width='100%' 
            cellSpacing='0' 
            cellPadding='10' 
            style={{ 
              borderCollapse: 'collapse', 
              borderColor: '#e2e8f0', 
              fontFamily: 'sans-serif',
              fontSize: '14px'
            }}
          >
            <thead style={{ background: '#1E3A8A', color: '#fff' }}>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Business</th>
                <th>Service Level</th>
                <th>Monthly Budget</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id='bmLeadRows'>
              <tr>
                <td colSpan='6' style={{ textAlign: 'center', color: '#94a3b8' }}>
                  No BM leads yet…
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <div id='bmModalOverlay' className='bm-modal-overlay' onClick={(e) => {
        if (e.target.id === 'bmModalOverlay') window.closeModal();
      }}>
        <div className='bm-modal'>
          <h3 id='bmModalTitle'>Submission Details</h3>
          <div id='bmModalBody'></div>
          <div style={{ textAlign: 'right', marginTop: '16px' }}>
            <button onClick={() => window.closeModal()} className='bm-btn' style={{ background: '#e2e8f0', color: '#0f172a' }}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer 
        style={{ 
          background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)', 
          padding: '48px 20px', 
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}
      >
        <h3 style={{ color: '#1E3A8A', marginBottom: '8px', fontSize: '1.5rem', fontWeight: 'bold' }}>
          BM-Assist.co
        </h3>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
          Restricted access — authorized BM personnel only.
        </p>
      </footer>
    </div>
  );
};

export default AdminPage;
